import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Download,
  Heart,
  Library,
  Loader2,
  Play,
  Search,
  ShoppingBag,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useToast } from '@/components/ui/use-toast';
import MapleMarketNavbar, { MAPLE_MARKET_NAV_TOTAL_HEIGHT } from '@/components/maple-market/MapleMarketNavbar';
import MapleMarketFooter from '@/components/maple-market/MapleMarketFooter';
import { useMapleMarketLibrary } from '@/hooks/useMapleMarketLibrary';
import { useAuth } from '@/contexts/AuthContext';
import { useMapleMarketInstaller } from '@/OS/utils/MapleMarketInstaller';
import { supabase } from '@/integrations/supabase/client';
import { useOsLicense } from '@/hooks/supabase/useOsLicense';
import type { MapleMarketCategory, MapleOSEditionSku } from '@/data/mapleMarketApps';
import type { MapleMarketInfoPageId } from '@/components/maple-market/MapleMarketInfoView';

type LibraryFilter = 'all' | 'installed' | 'ready' | 'favorites' | 'hidden';

interface MapleMarketLibraryViewProps {
  appId?: string;
  embedded?: boolean;
  isAdminAvailable?: boolean;
  onOpenAdmin?: () => void;
  onOpenLibrary?: (appId?: string) => void;
  onOpenStoreApp?: (appId: string) => void;
  onBackToStore?: () => void;
  onSelectCategory: (category: MapleMarketCategory | 'All') => void;
  onOpenPage?: (page: MapleMarketInfoPageId) => void;
}

const INSTALL_STAGE_LABELS: Record<string, string> = {
  requesting_download: 'Requesting download',
  downloading_package: 'Downloading',
  validating_manifest: 'Validating package',
  installing_files: 'Installing',
  recording_install: 'Recording install',
  launching_app: 'Launching',
  ready: 'Ready',
};

const MAPLE_OS_EDITION_LABELS: Record<string, string> = {
  'mapleos-26': 'MapleOS 26',
  'mapleos-pro-26': 'MapleOS Pro 26',
  'mapleos-enterprise-26': 'MapleOS Enterprise 26',
};

function formatDateTime(value: string | null | undefined) {
  if (!value) {
    return '—';
  }

  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(new Date(value));
}

function formatDate(value: string | null | undefined) {
  if (!value) {
    return '—';
  }

  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(new Date(value));
}

function getVisibilityLabel(status: 'hidden' | 'unpublished' | 'published') {
  if (status === 'published') {
    return 'Published';
  }

  return status === 'hidden' ? 'Hidden' : 'Unpublished';
}

export default function MapleMarketLibraryView({
  appId,
  embedded = false,
  isAdminAvailable = false,
  onOpenAdmin,
  onOpenLibrary,
  onOpenStoreApp,
  onBackToStore,
  onSelectCategory,
  onOpenPage,
}: MapleMarketLibraryViewProps) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  const { sku: osLicenseSku } = useOsLicense();
  const { installProduct, runInstalledModule } = useMapleMarketInstaller();
  const { entries, stats, loading, error, toggleWishlist } = useMapleMarketLibrary();
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState<LibraryFilter>('all');
  const [installingSku, setInstallingSku] = useState<string | null>(null);
  const [installStage, setInstallStage] = useState<string | null>(null);

  const openLibraryEntry = (nextAppId?: string) => {
    if (onOpenLibrary) {
      onOpenLibrary(nextAppId);
      return;
    }

    navigate(nextAppId ? `/market/library/${nextAppId}` : '/market/library');
  };

  const openStoreApp = (nextAppId: string) => {
    if (onOpenStoreApp) {
      onOpenStoreApp(nextAppId);
      return;
    }

    navigate(`/market/app/${nextAppId}`);
  };

  const backToStore = () => {
    if (onBackToStore) {
      onBackToStore();
      return;
    }

    navigate('/market');
  };

  const handleAuthAction = () => {
    if (embedded) {
      toast({
        title: 'Sign in required',
        description: 'Sign in to use your Maple Market library.',
        variant: 'destructive',
      });
      return;
    }

    navigate('/auth');
  };

  const filteredEntries = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();

    return entries.filter((entry) => {
      const matchesQuery =
        normalizedQuery.length === 0 ||
        entry.product.name.toLowerCase().includes(normalizedQuery) ||
        entry.product.developer.toLowerCase().includes(normalizedQuery) ||
        entry.product.tags.some((tag) => tag.toLowerCase().includes(normalizedQuery));

      if (!matchesQuery) {
        return false;
      }

      switch (filter) {
        case 'installed':
          return entry.installed;
        case 'ready':
          return entry.owned && !entry.installed;
        case 'favorites':
          return entry.favorite;
        case 'hidden':
          return entry.product.visibilityStatus !== 'published';
        default:
          return true;
      }
    });
  }, [entries, filter, query]);

  const selectedEntry = useMemo(() => {
    return entries.find((entry) => entry.product.id === appId) ?? filteredEntries[0] ?? null;
  }, [appId, entries, filteredEntries]);

  const relatedEntries = useMemo(() => {
    if (!selectedEntry) {
      return [];
    }

    return entries.filter((entry) => entry.product.id !== selectedEntry.product.id).slice(0, 6);
  }, [entries, selectedEntry]);

  const currentEdition: MapleOSEditionSku = osLicenseSku === 'mapleos-enterprise-26'
    ? 'mapleos-enterprise-26'
    : osLicenseSku === 'mapleos-pro-26'
      ? 'mapleos-pro-26'
      : 'mapleos-26';

  const supportedEditionLabels = selectedEntry?.product.supportedMapleOSEditions.map(
    (edition) => MAPLE_OS_EDITION_LABELS[edition] ?? edition,
  ) ?? [];
  const libraryLayoutClass = embedded
    ? 'md:grid-cols-[250px_minmax(0,1fr)]'
    : 'lg:grid-cols-[270px_minmax(0,1fr)] xl:grid-cols-[290px_minmax(0,1fr)]';
  const detailsLayoutClass = embedded
    ? 'lg:grid-cols-[minmax(0,1fr)_280px]'
    : 'xl:grid-cols-[minmax(0,1fr)_320px]';
  const libraryListHeightClass = embedded
    ? 'max-h-[calc(100vh-11.5rem)]'
    : 'max-h-[calc(100vh-14rem)]';

  const canInstallSelected = Boolean(
    selectedEntry?.product.packageType !== 'none' &&
    selectedEntry?.product.packagePath &&
    selectedEntry?.product.supportedMapleOSEditions.includes(currentEdition),
  );

  const selectedPackageLabel = selectedEntry?.product.packageType === 'package' ? '.mpak package' : 'HTML bundle';

  const handleInstall = async () => {
    if (!selectedEntry || !user) {
      handleAuthAction();
      return;
    }

    const { product } = selectedEntry;
    if (!product.packagePath || product.packageType === 'none') {
      toast({
        title: 'Install not available',
        description: 'This app does not have an install package yet.',
      });
      return;
    }

    if (!product.supportedMapleOSEditions.includes(currentEdition)) {
      toast({
        title: 'This app is not supported on your MapleOS edition',
        description: `Install this app on ${supportedEditionLabels.join(', ')}.`,
        variant: 'destructive',
      });
      return;
    }

    try {
      setInstallingSku(product.sku);
      setInstallStage('requesting_download');

      const { data, error: invokeError } = await supabase.functions.invoke('request-marketplace-download', {
        body: { sku: product.sku },
      });

      if (invokeError || !data?.url || !data?.version) {
        throw new Error(invokeError?.message || 'Unable to request install package.');
      }

      await installProduct({
        product,
        signedUrl: data.url,
        version: data.version,
        onProgress: setInstallStage,
      });

      toast({
        title: 'Installed',
        description: `${product.name} is ready to use.`,
      });
    } catch (installError) {
      toast({
        title: 'Install failed',
        description: installError instanceof Error ? installError.message : 'Please try again.',
        variant: 'destructive',
      });
    } finally {
      setInstallingSku(null);
      setInstallStage(null);
    }
  };

  const handlePlay = () => {
    if (!selectedEntry?.installRecord) {
      return;
    }

    runInstalledModule({
      productName: selectedEntry.product.name,
      rootPath: selectedEntry.installRecord.rootPath,
      embedded: true,
      sku: selectedEntry.product.sku,
    });
  };

  const handleToggleFavorite = async () => {
    if (!selectedEntry) {
      return;
    }

    try {
      const next = await toggleWishlist(selectedEntry.product.sku);
      toast({
        title: next ? 'Added to favorites' : 'Removed from favorites',
        description: next
          ? `${selectedEntry.product.name} is now in your favorites.`
          : `${selectedEntry.product.name} was removed from your favorites.`,
      });
    } catch (wishlistError) {
      toast({
        title: 'Could not update favorites',
        description: wishlistError instanceof Error ? wishlistError.message : 'Please try again.',
        variant: 'destructive',
      });
    }
  };

  return (
    <div className={embedded ? 'min-h-full bg-background' : 'min-h-screen bg-background'}>
      <MapleMarketNavbar
        products={entries.map((entry) => entry.product)}
        embedded={embedded}
        isAdminAvailable={isAdminAvailable}
        onOpenAdmin={onOpenAdmin}
        onOpenLibrary={() => openLibraryEntry()}
        onSelectCategory={onSelectCategory}
        onSelectApp={openStoreApp}
        activeSection="library"
      />

      <main
        className="container pb-8"
        style={embedded ? { paddingTop: '1.5rem' } : { paddingTop: `calc(${MAPLE_MARKET_NAV_TOTAL_HEIGHT} + 1.5rem)` }}
      >
        {!user ? (
          <Card>
            <CardHeader>
              <CardTitle>Sign in to open your library</CardTitle>
              <CardDescription>Your library shows apps you own or already installed.</CardDescription>
            </CardHeader>
            <CardContent>
              <Button onClick={handleAuthAction}>Sign in</Button>
            </CardContent>
          </Card>
        ) : entries.length === 0 && !loading ? (
          <Card>
            <CardHeader>
              <CardTitle>Your library is empty</CardTitle>
              <CardDescription>Buy an app, redeem a key, or install a free app to add it here.</CardDescription>
            </CardHeader>
            <CardContent className="flex gap-3">
              <Button onClick={backToStore}>
                <ShoppingBag className="mr-2 h-4 w-4" />
                Browse Store
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className={`grid gap-6 md:items-start ${libraryLayoutClass}`}>
            <aside className="overflow-hidden rounded-2xl border border-border bg-card/80 shadow-sm backdrop-blur md:sticky md:top-6">
              <div className="border-b border-border p-3">
                <div className="flex items-center gap-2">
                  <Library className="h-4 w-4 text-primary" />
                  <h1 className="text-lg font-semibold text-foreground">Library</h1>
                </div>
                <div className="mt-2 text-xs text-muted-foreground">
                  {stats.total} apps • {stats.installed} installed • {stats.readyToInstall} ready
                </div>
                <div className="relative mt-3">
                  <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search your library" className="h-9 pl-9" />
                </div>
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {[
                    ['all', 'All'],
                    ['installed', 'Installed'],
                    ['ready', 'Ready'],
                    ['favorites', 'Favorites'],
                    ['hidden', 'Hidden'],
                  ].map(([value, label]) => (
                    <button
                      key={value}
                      type="button"
                      onClick={() => setFilter(value as LibraryFilter)}
                      className={`rounded-full px-2.5 py-1 text-[11px] font-medium leading-none transition-colors ${
                        filter === value
                          ? 'bg-primary text-primary-foreground'
                          : 'bg-secondary text-secondary-foreground hover:bg-secondary/80'
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              <div className={`${libraryListHeightClass} overflow-y-auto p-2`}>
                {filteredEntries.map((entry) => {
                  const selected = selectedEntry?.product.id === entry.product.id;

                  return (
                    <button
                      key={entry.product.id}
                      type="button"
                      onClick={() => openLibraryEntry(entry.product.id)}
                      className={`mb-1.5 flex w-full items-center gap-2.5 rounded-lg border px-2.5 py-2 text-left transition-colors ${
                        selected
                          ? 'border-primary/30 bg-primary/10 text-foreground shadow-sm'
                          : 'border-transparent text-muted-foreground hover:border-border hover:bg-accent hover:text-foreground'
                      }`}
                    >
                      <img src={entry.product.image} alt={entry.product.name} className="h-10 w-10 rounded-md object-cover" />
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-sm font-medium leading-tight">{entry.product.name}</div>
                        <div className="truncate text-[11px] leading-tight text-muted-foreground">{entry.product.developer}</div>
                        <div className="mt-1 flex flex-wrap items-center gap-1 text-[10px]">
                          {entry.installed && <span className="rounded-full bg-emerald-500/10 px-1.5 py-0.5 text-emerald-600">Installed</span>}
                          {!entry.installed && entry.owned && <span className="rounded-full bg-blue-500/10 px-1.5 py-0.5 text-blue-600">Ready</span>}
                          {entry.favorite && <span className="rounded-full bg-rose-500/10 px-1.5 py-0.5 text-rose-600">Favorite</span>}
                        </div>
                      </div>
                    </button>
                  );
                })}

                {!loading && filteredEntries.length === 0 && (
                  <div className="rounded-xl border border-dashed border-border p-4 text-sm text-muted-foreground">
                    Nothing matches this filter.
                  </div>
                )}
              </div>
            </aside>

            <section className="min-w-0 space-y-6">
              {selectedEntry ? (
                <>
                  <div className="overflow-hidden rounded-2xl border border-border bg-card shadow-sm">
                    <div className="relative min-h-[260px] overflow-hidden">
                      <div className="absolute inset-0">
                        <img src={selectedEntry.product.heroImageUrl || selectedEntry.product.image} alt={selectedEntry.product.name} className="h-full w-full object-cover" />
                        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/70 to-background/35 dark:via-background/65 dark:to-background/20" />
                      </div>
                      <div className="relative flex min-h-[260px] flex-col justify-end p-6">
                        <div className="max-w-4xl rounded-2xl border border-border/80 bg-background/85 p-5 shadow-lg backdrop-blur-md dark:border-white/10 dark:bg-background/55">
                          <div className="flex flex-wrap items-center gap-2">
                            {selectedEntry.installed ? <Badge>Installed</Badge> : <Badge variant="secondary">Ready to install</Badge>}
                            {selectedEntry.favorite && <Badge variant="outline">Favorite</Badge>}
                            {selectedEntry.product.visibilityStatus !== 'published' && (
                              <Badge variant="outline">{getVisibilityLabel(selectedEntry.product.visibilityStatus)}</Badge>
                            )}
                          </div>
                          <div className="mt-4">
                            <div className="mb-2 text-xs font-medium uppercase tracking-[0.2em] text-muted-foreground">Selected app</div>
                            <h2 className="text-4xl font-bold tracking-tight text-foreground">{selectedEntry.product.name}</h2>
                            <p className="mt-2 max-w-3xl text-sm text-muted-foreground">{selectedEntry.product.description}</p>
                          </div>
                          <div className="mt-5 flex flex-wrap items-center gap-3">
                            {selectedEntry.installed ? (
                              <Button size="lg" onClick={handlePlay}>
                                <Play className="mr-2 h-4 w-4" />
                                Play
                              </Button>
                            ) : (
                              <Button size="lg" onClick={() => void handleInstall()} disabled={!canInstallSelected || installingSku === selectedEntry.product.sku}>
                                {installingSku === selectedEntry.product.sku ? (
                                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                ) : (
                                  <Download className="mr-2 h-4 w-4" />
                                )}
                                {installingSku === selectedEntry.product.sku
                                  ? INSTALL_STAGE_LABELS[installStage || 'requesting_download'] || 'Installing'
                                  : 'Install'}
                              </Button>
                            )}
                            <Button size="lg" variant="secondary" onClick={handleToggleFavorite}>
                              <Heart className={`mr-2 h-4 w-4 ${selectedEntry.favorite ? 'fill-current' : ''}`} />
                              {selectedEntry.favorite ? 'Favorite' : 'Add to favorites'}
                            </Button>
                            <Button size="lg" variant="outline" onClick={() => openStoreApp(selectedEntry.product.id)}>
                              Store Page
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="grid gap-4 border-t border-border p-6 md:grid-cols-4">
                      <div>
                        <div className="text-xs uppercase tracking-wide text-muted-foreground">Developer</div>
                        <div className="mt-1 text-sm font-medium text-foreground">{selectedEntry.product.developer}</div>
                      </div>
                      <div>
                        <div className="text-xs uppercase tracking-wide text-muted-foreground">Last played</div>
                        <div className="mt-1 text-sm font-medium text-foreground">{formatDateTime(selectedEntry.installRecord?.lastPlayedAt)}</div>
                      </div>
                      <div>
                        <div className="text-xs uppercase tracking-wide text-muted-foreground">Installed</div>
                        <div className="mt-1 text-sm font-medium text-foreground">{formatDateTime(selectedEntry.installRecord?.installedAt)}</div>
                      </div>
                      <div>
                        <div className="text-xs uppercase tracking-wide text-muted-foreground">Release date</div>
                        <div className="mt-1 text-sm font-medium text-foreground">{formatDate(selectedEntry.product.releaseDate)}</div>
                      </div>
                    </div>
                  </div>

                  <div className={`grid gap-6 ${detailsLayoutClass}`}>
                    <div className="space-y-6">
                      <Card>
                        <CardHeader>
                          <CardTitle>About</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                          <p className="whitespace-pre-line text-sm leading-7 text-muted-foreground">{selectedEntry.product.longDescription}</p>
                          {selectedEntry.product.publishRejectionReason && (
                            <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-200">
                              <div className="font-medium">Review note</div>
                              <div className="mt-1">{selectedEntry.product.publishRejectionReason}</div>
                            </div>
                          )}
                        </CardContent>
                      </Card>

                      <Card>
                        <CardHeader>
                          <CardTitle>Recent activity</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-3">
                          <div className="rounded-xl border border-border p-4">
                            <div className="text-xs uppercase tracking-wide text-muted-foreground">Version</div>
                            <div className="mt-1 text-base font-medium text-foreground">{selectedEntry.product.version}</div>
                            <div className="mt-1 text-sm text-muted-foreground">Available since {formatDate(selectedEntry.product.releaseDate)}</div>
                          </div>
                          <div className="rounded-xl border border-border p-4">
                            <div className="text-xs uppercase tracking-wide text-muted-foreground">Status</div>
                            <div className="mt-1 text-base font-medium text-foreground">
                              {selectedEntry.installed ? 'Installed on this MapleOS device' : 'Ready to install'}
                            </div>
                            <div className="mt-1 text-sm text-muted-foreground">
                              {selectedEntry.installed
                                ? `Installed ${formatDateTime(selectedEntry.installRecord?.installedAt)}`
                                : `Use Install to download and set up this ${selectedPackageLabel}.`}
                            </div>
                          </div>
                        </CardContent>
                      </Card>

                      {relatedEntries.length > 0 && (
                        <Card>
                          <CardHeader>
                            <CardTitle>More in your library</CardTitle>
                          </CardHeader>
                          <CardContent>
                            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                              {relatedEntries.map((entry) => (
                                <button
                                  key={entry.product.id}
                                  type="button"
                                  onClick={() => openLibraryEntry(entry.product.id)}
                                  className="overflow-hidden rounded-xl border border-border bg-background text-left transition-colors hover:border-primary/40"
                                >
                                  <img src={entry.product.image} alt={entry.product.name} className="aspect-[16/9] w-full object-cover" />
                                  <div className="p-3">
                                    <div className="truncate font-medium text-foreground">{entry.product.name}</div>
                                    <div className="mt-1 text-xs text-muted-foreground">{entry.installed ? 'Installed' : 'Ready to install'}</div>
                                  </div>
                                </button>
                              ))}
                            </div>
                          </CardContent>
                        </Card>
                      )}
                    </div>

                    <div className="space-y-6">
                      <Card>
                        <CardHeader>
                          <CardTitle>Library details</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4 text-sm">
                          <div className="flex items-center justify-between gap-4">
                            <span className="text-muted-foreground">Category</span>
                            <span className="font-medium text-foreground">{selectedEntry.product.category}</span>
                          </div>
                          <div className="flex items-center justify-between gap-4">
                            <span className="text-muted-foreground">Size</span>
                            <span className="font-medium text-foreground">{selectedEntry.product.size}</span>
                          </div>
                          <div className="flex items-center justify-between gap-4">
                            <span className="text-muted-foreground">Visibility</span>
                            <span className="font-medium text-foreground">{getVisibilityLabel(selectedEntry.product.visibilityStatus)}</span>
                          </div>
                          <div className="flex items-center justify-between gap-4">
                            <span className="text-muted-foreground">Install path</span>
                            <span className="max-w-[180px] truncate text-right font-medium text-foreground">{selectedEntry.installRecord?.rootPath || 'Not installed'}</span>
                          </div>
                        </CardContent>
                      </Card>

                      <Card>
                        <CardHeader>
                          <CardTitle>Supported on</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-2 text-sm text-muted-foreground">
                          {supportedEditionLabels.map((edition) => (
                            <div key={edition} className="rounded-lg border border-border px-3 py-2 text-foreground">
                              {edition}
                            </div>
                          ))}
                        </CardContent>
                      </Card>

                      <Card>
                        <CardHeader>
                          <CardTitle>Tags</CardTitle>
                        </CardHeader>
                        <CardContent className="flex flex-wrap gap-2">
                          {selectedEntry.product.tags.map((tag) => (
                            <Badge key={tag} variant="secondary">{tag}</Badge>
                          ))}
                        </CardContent>
                      </Card>
                    </div>
                  </div>
                </>
              ) : (
                <Card>
                  <CardHeader>
                    <CardTitle>No apps found</CardTitle>
                    <CardDescription>Try another filter or browse the store.</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Button onClick={backToStore}>Browse Store</Button>
                  </CardContent>
                </Card>
              )}

              {(loading || error) && (
                <div className="rounded-xl border border-border bg-card p-4 text-sm text-muted-foreground">
                  {loading ? 'Loading your library…' : error}
                </div>
              )}
            </section>
          </div>
        )}
      </main>

      <MapleMarketFooter onSelectCategory={onSelectCategory} onOpenPage={onOpenPage} />
    </div>
  );
}